// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Container, Header, Form, Segment, Message, Loader, Label, Icon, Table } from 'semantic-ui-react';
import { loaAdminApi, TemplateResolutionInfo } from '../api/loaAdminApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';

/**
 * Phase 4: Template Preview & Reporting
 * Preview which template will be used for a given brokerage and policy type combination
 */
function TemplateResolutionPreviewPage() {
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [selectedBrokerageId, setSelectedBrokerageId] = useState<string>('');
  const [selectedPolicyTypeId, setSelectedPolicyTypeId] = useState<string>('');
  const [resolutionInfo, setResolutionInfo] = useState<TemplateResolutionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  // Load brokerages and policy types on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [brokeragesData, policyTypesData] = await Promise.all([
          brokerageApi.getAll({ size: 100 }),
          policyTypeApi.getAll({ size: 100 })
        ]);
        setBrokerages(brokeragesData.content);
        setPolicyTypes(policyTypesData.content);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, []);

  // Preview template resolution when both selections are made
  useEffect(() => {
    const previewResolution = async () => {
      if (!selectedBrokerageId || !selectedPolicyTypeId) {
        setResolutionInfo(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const info = await loaAdminApi.previewTemplateResolution(
          selectedBrokerageId,
          selectedPolicyTypeId
        );
        setResolutionInfo(info);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to preview template resolution');
        setResolutionInfo(null);
      } finally {
        setLoading(false);
      }
    };

    previewResolution();
  }, [selectedBrokerageId, selectedPolicyTypeId]);

  const brokerageOptions = brokerages.map(b => ({
    key: b.id,
    text: b.name,
    value: b.id
  }));

  const policyTypeOptions = policyTypes.map(pt => ({
    key: pt.id,
    text: `${pt.name} (${pt.providerName})`,
    value: pt.id
  }));

  const getStatusLabel = (resolvedSource: string, currentSource: string, hasTemplate: boolean) => {
    if (resolvedSource === currentSource) {
      return (
        <Label color="green">
          <Icon name="check" /> Selected
        </Label>
      );
    }
    if (hasTemplate) {
      return <Label basic>Available</Label>;
    }
    return <Label basic color="grey">Not Set</Label>;
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'BROKERAGE_CUSTOM':
        return { text: 'Brokerage Custom', color: 'blue' as const };
      case 'POLICY_TYPE_DEFAULT':
        return { text: 'Policy Type Default', color: 'teal' as const };
      case 'GLOBAL_DEFAULT':
        return { text: 'Global Default', color: 'grey' as const };
      default:
        return { text: source, color: 'grey' as const };
    }
  };

  if (loadingData) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Loader active>Loading brokerages and policy types...</Loader>
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: '2em' }}>
      <Header as="h1">
        <Icon name="search" />
        <Header.Content>
          Template Resolution Preview
          <Header.Subheader>
            Preview which template will be used for a specific brokerage and policy type combination
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment>
        <Form>
          <Form.Group widths="equal">
            <Form.Select
              label="Brokerage"
              placeholder="Select brokerage"
              options={brokerageOptions}
              value={selectedBrokerageId}
              onChange={(_, data) => setSelectedBrokerageId(data.value as string)}
              search
              required
            />
            <Form.Select
              label="Policy Type"
              placeholder="Select policy type"
              options={policyTypeOptions}
              value={selectedPolicyTypeId}
              onChange={(_, data) => setSelectedPolicyTypeId(data.value as string)}
              search
              required
            />
          </Form.Group>
        </Form>
      </Segment>

      {loading && (
        <Segment>
          <Loader active>Previewing template resolution...</Loader>
        </Segment>
      )}

      {!loading && resolutionInfo && (
        <Segment>
          <Header as="h3">
            <Icon name="check circle" color="green" />
            Resolved Template
          </Header>
          <Table definition>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Template Name</Table.Cell>
                <Table.Cell>
                  <strong>{resolutionInfo.resolvedTemplateName}</strong>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Template ID</Table.Cell>
                <Table.Cell>
                  <code>{resolutionInfo.resolvedTemplateId}</code>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Resolution Source</Table.Cell>
                <Table.Cell>
                  <Label {...getSourceLabel(resolutionInfo.resolvedSource)}>
                    {getSourceLabel(resolutionInfo.resolvedSource).text}
                  </Label>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Header as="h3" style={{ marginTop: '2em' }}>
            <Icon name="list" />
            Template Hierarchy
          </Header>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Priority</Table.HeaderCell>
                <Table.HeaderCell>Level</Table.HeaderCell>
                <Table.HeaderCell>Template</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row positive={resolutionInfo.resolvedSource === 'BROKERAGE_CUSTOM'}>
                <Table.Cell>1</Table.Cell>
                <Table.Cell>
                  <Label color="blue">Brokerage Custom</Label>
                </Table.Cell>
                <Table.Cell>
                  {resolutionInfo.brokerageCustomTemplateName ? (
                    <>
                      <strong>{resolutionInfo.brokerageCustomTemplateName}</strong>
                      <br />
                      <code style={{ fontSize: '0.85em' }}>{resolutionInfo.brokerageCustomTemplateId}</code>
                    </>
                  ) : (
                    <em style={{ color: '#999' }}>Not configured</em>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {getStatusLabel(resolutionInfo.resolvedSource, 'BROKERAGE_CUSTOM', !!resolutionInfo.brokerageCustomTemplateName)}
                </Table.Cell>
              </Table.Row>

              <Table.Row positive={resolutionInfo.resolvedSource === 'POLICY_TYPE_DEFAULT'}>
                <Table.Cell>2</Table.Cell>
                <Table.Cell>
                  <Label color="teal">Policy Type Default</Label>
                </Table.Cell>
                <Table.Cell>
                  {resolutionInfo.policyTypeDefaultTemplateName ? (
                    <>
                      <strong>{resolutionInfo.policyTypeDefaultTemplateName}</strong>
                      <br />
                      <code style={{ fontSize: '0.85em' }}>{resolutionInfo.policyTypeDefaultTemplateId}</code>
                    </>
                  ) : (
                    <em style={{ color: '#999' }}>Not configured</em>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {getStatusLabel(resolutionInfo.resolvedSource, 'POLICY_TYPE_DEFAULT', !!resolutionInfo.policyTypeDefaultTemplateName)}
                </Table.Cell>
              </Table.Row>

              <Table.Row positive={resolutionInfo.resolvedSource === 'GLOBAL_DEFAULT'}>
                <Table.Cell>3</Table.Cell>
                <Table.Cell>
                  <Label color="grey">Global Default</Label>
                </Table.Cell>
                <Table.Cell>
                  {resolutionInfo.globalDefaultTemplateName ? (
                    <>
                      <strong>{resolutionInfo.globalDefaultTemplateName}</strong>
                      <br />
                      <code style={{ fontSize: '0.85em' }}>{resolutionInfo.globalDefaultTemplateId}</code>
                    </>
                  ) : (
                    <em style={{ color: '#999' }}>Not configured</em>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {getStatusLabel(resolutionInfo.resolvedSource, 'GLOBAL_DEFAULT', !!resolutionInfo.globalDefaultTemplateName)}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Message info>
            <Message.Header>Template Resolution Logic</Message.Header>
            <p>
              The system follows a waterfall hierarchy to resolve which template to use:
            </p>
            <ol>
              <li><strong>Brokerage Custom:</strong> First checks if the brokerage has a custom template configured</li>
              <li><strong>Policy Type Default:</strong> Falls back to the policy type's default template if no brokerage custom template</li>
              <li><strong>Global Default:</strong> Uses the platform-wide default template as last resort</li>
            </ol>
          </Message>
        </Segment>
      )}

      {!loading && !resolutionInfo && selectedBrokerageId && selectedPolicyTypeId && !error && (
        <Message warning>
          <Message.Header>No Template Found</Message.Header>
          <p>No template configuration could be resolved for this combination.</p>
        </Message>
      )}

      {!selectedBrokerageId || !selectedPolicyTypeId && (
        <Message info>
          <Message.Header>Select Brokerage and Policy Type</Message.Header>
          <p>Choose a brokerage and policy type to preview which template will be used.</p>
        </Message>
      )}
    </Container>
  );
}

export default TemplateResolutionPreviewPage;
